/**
 * Random YAML Generator Tool
 *
 * Generates deterministic (seeded) YAML for test data.
 */

import { stringify as stringifyYaml } from 'yaml';

import { SEMANTIC_TYPES } from '@/detectors/semanticTypes';
import { ToolError } from '@/engine/errors/ToolError';
import type { IGeneratorTool } from '@/interfaces/ITool';

type YamlNode = null | boolean | number | string | YamlNode[] | { [k: string]: YamlNode };

export const RandomYamlGeneratorTool: IGeneratorTool = {
  id: 'random-yaml-generator',
  name: 'Random YAML',
  description: 'Generate deterministic (seeded) random YAML for testing',
  version: '1.0.0',
  type: 'generator',
  category: 'Generators',
  keywords: ['yaml', 'generate', 'random', 'seed', 'test-data', 'generator'],

  outputType: SEMANTIC_TYPES.YAML,

  attributes: [
    {
      name: 'seed',
      type: 'number',
      label: 'Seed',
      defaultValue: 42,
      validation: { min: 0, max: 2147483647 },
      description: 'Seed for deterministic output (same seed = same YAML)',
    },
    {
      name: 'entries',
      type: 'number',
      label: 'Entries',
      defaultValue: 5,
      validation: { min: 1, max: 100 },
      description: 'Number of top-level keys to generate',
    },
    {
      name: 'maxDepth',
      type: 'number',
      label: 'Max Depth',
      defaultValue: 2,
      validation: { min: 0, max: 5 },
      description: 'Maximum nesting depth for generated objects/arrays',
    },
    {
      name: 'includeArrays',
      type: 'boolean',
      label: 'Include Arrays',
      defaultValue: true,
      description: 'Allow arrays in generated output',
    },
  ],

  async generate(config: Record<string, unknown>): Promise<string> {
    const seedRaw = config.seed;
    const entriesRaw = config.entries;
    const maxDepthRaw = config.maxDepth;

    const seed = normalizeInt(seedRaw, 42, 0, 2147483647, 'seed');
    const entries = normalizeInt(entriesRaw, 5, 1, 100, 'entries');
    const maxDepth = normalizeInt(maxDepthRaw, 2, 0, 5, 'maxDepth');
    const includeArrays = config.includeArrays !== false;

    const rng = createRng(seed);

    const out: Record<string, YamlNode> = {};
    for (let i = 0; i < entries; i++) {
      const key = `key${i + 1}`;
      out[key] = generateNode(rng, 0, maxDepth, includeArrays);
    }

    try {
      return stringifyYaml(out, { indent: 2 }).trimEnd();
    } catch (e) {
      const error = e as Error;
      throw ToolError.validation(`Invalid YAML generation: ${error.message}`);
    }
  },
};

function normalizeInt(
  value: unknown,
  defaultValue: number,
  min: number,
  max: number,
  name: string
): number {
  const n = typeof value === 'number' ? value : Number(value);
  const resolved = Number.isFinite(n) ? Math.trunc(n) : defaultValue;

  if (resolved < min || resolved > max) {
    throw ToolError.validation(`Invalid ${name}: expected an integer between ${min} and ${max}`);
  }

  return resolved;
}

function createRng(seed: number): () => number {
  // xorshift32
  let x = seed | 0;
  if (x === 0) x = 0x1a2b3c4d;

  return () => {
    x ^= x << 13;
    x ^= x >>> 17;
    x ^= x << 5;
    // Convert to [0, 1)
    return ((x >>> 0) & 0xffffffff) / 0x100000000;
  };
}

function generateNode(rng: () => number, depth: number, maxDepth: number, includeArrays: boolean): YamlNode {
  if (depth >= maxDepth) {
    return generateScalar(rng);
  }

  const pick = rng();

  // Favor scalars for readability
  if (pick < 0.6) {
    return generateScalar(rng);
  }

  if (includeArrays && pick < 0.8) {
    const len = 1 + Math.floor(rng() * 4);
    const arr: YamlNode[] = [];
    for (let i = 0; i < len; i++) {
      arr.push(generateNode(rng, depth + 1, maxDepth, includeArrays));
    }
    return arr;
  }

  const count = 1 + Math.floor(rng() * 4);
  const obj: Record<string, YamlNode> = {};
  for (let i = 0; i < count; i++) {
    obj[`k${depth + 1}_${i + 1}`] = generateNode(rng, depth + 1, maxDepth, includeArrays);
  }
  return obj;
}

function generateScalar(rng: () => number): YamlNode {
  const pick = rng();

  if (pick < 0.15) return null;
  if (pick < 0.35) return pick < 0.25;
  if (pick < 0.65) return Math.floor(rng() * 1000);

  return generateString(rng);
}

function generateString(rng: () => number): string {
  const alphabet = 'abcdefghijklmnopqrstuvwxyz';
  const len = 3 + Math.floor(rng() * 8);
  let out = '';
  for (let i = 0; i < len; i++) {
    out += alphabet[Math.floor(rng() * alphabet.length)];
  }
  return out;
}
